import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import useU2000AxiosAuthenticated from "../u2000Core/axios/U2000UseAxiosAuthenticated";
import { U2000HandleApiError } from "../u2000Core/axios/U2000HandleApiError";
import { useToast } from "../u2000Core/context/toast/U2000ToastContext";
import type { U2000AutoCompleteSearchResult } from "../u2000Core/types/U2000AutoCompleteSearchResult";
import { u2000ExtractAutoCompleteResults } from "../u2000Core/utils/u2000ExtractAutoCompleteResults";
import { NS } from "./U3139";
import { doFindCadastres, luHandleAppiError, type U3139FindCadastreCriterias, type U3139FindCadastreItem } from "./U3139Service";

export interface U3139FindCadastreHandlersParams {
  getDistrictId: () => string | number | undefined | null;
}

/** Autocomplete cadastre via POST /U3139/findCadastres (filtré par districtId). */
export function useU3139FindCadastreHandlers(params: U3139FindCadastreHandlersParams) {
  const { getDistrictId } = params;
  const axios = useU2000AxiosAuthenticated();
  const { t } = useTranslation();
  const toast = useToast();

  const search = useCallback(
    async (criterias: U3139FindCadastreCriterias): Promise<U2000AutoCompleteSearchResult<U3139FindCadastreItem>> => {
      const districtId = getDistrictId()?.toString().trim();
      if (!districtId) {
        return { results: [], totalCount: 0, truncated: false };
      }

      try {
        const criteriasWithDistrict: U3139FindCadastreCriterias = [
          ...criterias,
          { fieldName: "districtId", value: districtId },
        ];
        const response = await doFindCadastres(axios, criteriasWithDistrict);
        return u2000ExtractAutoCompleteResults<U3139FindCadastreItem>(response.data);
      } catch (error: unknown) {
        const errorResult = U2000HandleApiError(error as Error, t, NS, luHandleAppiError);
        if (errorResult.genericErrors?.length) {
          errorResult.genericErrors.forEach((err) => toast.error(err.text, err.title));
        }
        return { results: [], totalCount: 0, truncated: false };
      }
    },
    [axios, t, toast, getDistrictId],
  );

  return { search };
}
